import { store } from "commonui";
import { VuexModule, Module, getModule, Action, Mutation } from "vuex-module-decorators";
import { LanguageSkill, MediaSlot, MethodologyTypeEnum, ParentType } from "@/util/enum";
import TitleListState from "@/modules/title-list";
import { ContentExposureService } from "@/services/content-exposure";
import { cloneDeep } from "lodash";
import { deepClone } from "@/util/func";

function toTimeString(second) {
	const sec = second % 60;
	return `${Math.floor(second / 60)}:${sec > 9 ? sec : `0${sec}`}`;
}
function toSeconds(time: string) {
	const times = time.split(":");
	return parseInt(times[0]) * 60 + parseInt(times[1]);
}

@Module({ namespaced: true, name: "REPState", store: store, dynamic: true })
class REPState extends VuexModule {
	repList: any[] = [];
	versionGroupId: string = "";
	contentId: string = "";
	vcsLevelId: string = "";
	titleListModule = getModule(TitleListState);
	defaultMediaSlot = MediaSlot.ExposureAudio;

	get getREPs() {
		return this.repList;
	}

	get getVersionGroupId() {
		return this.versionGroupId;
	}

	get getVcsLevelId() {
		return this.vcsLevelId;
	}

	@Mutation
	setContentId(contentId) {
		this.contentId = contentId;
	}

	@Mutation
	setREPList(repList: any[]) {
		this.repList = repList;
	}

	@Mutation
	setDefaultMediaSlot(mediaSlotId) {
		this.defaultMediaSlot = mediaSlotId;
	}

	@Mutation
	setContentVersionGroupId({ versionGroupId, contentId, vcsLevelId }) {
		this.versionGroupId = versionGroupId;
		this.contentId = contentId;
		this.vcsLevelId = vcsLevelId;
	}

	@Mutation
	setRepPlan(repPlan: any) {
		const index = this.repList.findIndex((d) => d.id === repPlan.id);
		const data = cloneDeep(this.repList[index]);
		Object.getOwnPropertyNames(repPlan).forEach((key) => {
			data[key] = repPlan[key];
		});
		this.repList.splice(index, 1, data);
	}

	@Mutation
	setContentExposure(rep: any) {
		const index = this.repList.findIndex((d) => d.id === rep.id);
		const data = cloneDeep(this.repList[index]);
		Object.getOwnPropertyNames(rep).forEach((key) => {
			data[key] = rep[key];
		});
		this.repList.splice(index, 1, data);
	}

	@Action
	setRelatedItems({ record: { id }, values }) {
		this.setRepPlan({ id, relatedItems: values });
	}

	@Action
	setMediaSlot({ record: { id }, values }) {
		this.setRepPlan({ id, mediaSlots: [values.target.value] });
	}

	@Action
	async updateExposure(record) {
		await this.editExposure(record);
		await this.fetchREPs(record.lessonPlanVersionId);
	}

	@Action
	async addREP({ lessonPlanVersionId, numberOfRepExposures, currentMediaSlot }) {
		const result = await this.createREP({ lessonPlanVersionId, numberOfRepExposures, currentMediaSlot });
		await this.fetchREPs(lessonPlanVersionId);
		return result;
	}

	@Action
	createREP({ lessonPlanVersionId, numberOfRepExposures, currentMediaSlot }) {
		const { editedContentDetails } = this.titleListModule;
		return ContentExposureService.create({
			contentId: this.contentId,
			minPlanningTime: editedContentDetails.mediaLength,
			maxPlanningTime: editedContentDetails.mediaLength,
			numberOfExposures: numberOfRepExposures,
			lessonPlanVersionId: lessonPlanVersionId,
			contentExposureMediaSlot: [{ mediaSlotId: currentMediaSlot }],
			isRepLessonPlanVersion: true
		});
	}

	@Action
	async fetchREPs(lessonPlanVersionId?: string) {
		const contentId = this.contentId;
		const repList = await ContentExposureService.getContentExposures({
			contentId,
			lessonPlanVersionId
		});
		this.formatREPs(repList);
		this.setREPList(repList);
	}

	@Action
	formatREPs(repList: any[]) {
		repList.forEach((d) => {
			d.key = d.id;
			d.duration = { start: toTimeString(d.minPlanningTime), end: toTimeString(d.maxPlanningTime) };
			d.relatedItems = d.contentExposureRelatedItem.map((item) => ({
				contentId: item.contentId,
				type: item.type,
				title: item.content?.title || item.text,
				vcsLevelId: item.content?.vcsLevelId,
				unitName: item.content?.vcsLevel?.displayName,
				unit: item.content?.vcsLevel?.unit,
				text: item.text,
				sequence: item.sequence,
				typeSequence: item.typeSequence,
				contentType: item.content?.contentType,
				typeName: item.content?.contentType?.name
			}));
			d.teachingActivities = d.contentExposureTeachingActivity;
			d.objectives = d.contentExposureObjective;
			d.mediaSlots = d.contentExposureMediaSlot.map((m) => m.mediaSlotId);
		});
	}

	@Action
	async deleteREP(record) {
		await this.removeREP(record);
		await this.fetchREPs(record.lessonPlanVersionId);
	}

	@Action
	removeREP({ id }) {
		return ContentExposureService.deleteContentExposure({ id });
	}

	@Action
	async copyREP({ recordIds, lessonPlanVersionId, targetLessonPlanVersionId }) {
		const result = await this.cloneREP({ recordIds, targetLessonPlanVersionId });
		await this.fetchREPs(lessonPlanVersionId);
		return result;
	}

	@Action
	cloneREP({ recordIds, targetLessonPlanVersionId }) {
		return ContentExposureService.duplicateContentExposure(recordIds, targetLessonPlanVersionId);
	}

	@Action
	editingExposure({ id, isEdit }) {
		this.setContentExposure({ id, isEdit });
	}

	@Action
	async fetchDefaultMediaSlot(contentId) {
		const mediaSlot = await ContentExposureService.getDefaultMediaSlot({ contentId });
		this.setDefaultMediaSlot(mediaSlot);
	}

	@Action
	async editExposure(record) {
		return ContentExposureService.update(
			((d) => {
				d.minPlanningTime = toSeconds(d.duration.start);
				d.maxPlanningTime = toSeconds(d.duration.end);
				d.contentExposureRelatedItem = d.relatedItems.map((item) => ({
					...item
				}));
				d.contentExposureMediaSlot = d.mediaSlots.map((m) => ({
					mediaSlotId: m
				}));
				d.isRepLessonPlanVersion = true;
				d.isExposureEdit = true;
				delete d.duration;
				delete d.isEdit;
				delete d.key;
				delete d.relatedItems;
				delete d.mediaSlots;
				return d;
			})(deepClone(record))
		);
	}

	@Action
	async getDefaultMediaSlot() {
		const { editedContentDetails } = this.titleListModule;
		if (editedContentDetails.languageSkillId === LanguageSkill.Read) {
			return MediaSlot.ViewingContent;
		} else {
			return MediaSlot.ExposureAudio;
		}
	}
}

export const REPModule = getModule(REPState);
